import { useMemo, useState } from 'react';
import { Archive, GripVertical, Loader2, Pencil, Plus, RotateCcw } from 'lucide-react';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    useTrialResultCodes,
    useCreateTrialResultCode,
    useUpdateTrialResultCode,
    useArchiveTrialResultCode,
    useRestoreTrialResultCode,
    useReorderTrialResultCodes,
    type TrialResultCode,
} from '@/api/trialResultCodes';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { FormField, Input } from '@/components/ui/FormField';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { RESULT_CODE_COLORS, DEFAULT_RESULT_CODE_COLOR } from '@/lib/resultCodeColors';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * ResultCodesPanel — Configuration → Clinical → Result Codes.
 *
 * Codes drive trial and task_analysis data entry. The `code` + `isCorrect`
 * fields are fixed on create and cannot be edited (see the trial_result_codes
 * migration rationale): flipping correctness on a saved row would silently
 * rewrite historical mastery math. Practices archive-and-replace instead.
 *
 * Archive is a soft-delete: the row stays so that historical value_json still
 * resolves to a label, but the code is hidden from pickers. Archived rows can
 * be restored from the Archived tab — because `code` is globally unique per
 * practice, restore is also the only way to "bring back" a retired code (the
 * server refuses a create with a clashing archived code).
 *
 * Ordering is drag-and-drop only — on the Active tab a grip handle reorders
 * rows via PUT /trial-result-codes/reorder. Archived rows keep their stored
 * sort_order (non-draggable); on the All tab they pin to the bottom.
 */
type Tab = 'Active' | 'Archived' | 'All';
const TABS: Tab[] = ['Active', 'Archived', 'All'];

export function ResultCodesPanel() {
    const { data, isLoading, isError, error } = useTrialResultCodes(true); // includeArchived
    const createM = useCreateTrialResultCode();
    const updateM = useUpdateTrialResultCode();
    const archiveM = useArchiveTrialResultCode();
    const restoreM = useRestoreTrialResultCode();
    const reorderM = useReorderTrialResultCodes();

    const [tab, setTab] = useState<Tab>('Active');
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState<TrialResultCode | null>(null);
    const [confirmArchive, setConfirmArchive] = useState<TrialResultCode | null>(null);

    const { activeRows, archivedRows } = useMemo(() => {
        if (!data) return { activeRows: [], archivedRows: [] };
        const sortBySortOrder = (a: TrialResultCode, b: TrialResultCode) =>
            a.sortOrder - b.sortOrder || a.code.localeCompare(b.code);
        return {
            activeRows:   data.filter((r) => !r.archivedAt).sort(sortBySortOrder),
            archivedRows: data.filter((r) =>  r.archivedAt).sort(sortBySortOrder),
        };
    }, [data]);

    const rows =
        tab === 'Active'   ? activeRows
      : tab === 'Archived' ? archivedRows
      :                      [...activeRows, ...archivedRows];

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    function handleDragEnd(e: DragEndEvent) {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIndex = activeRows.findIndex((r) => r.id === active.id);
        const newIndex = activeRows.findIndex((r) => r.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const nextIds = arrayMove(activeRows, oldIndex, newIndex).map((r) => r.id);
        reorderM.mutate(nextIds);
    }

    // Drag is only offered on the Active tab; the grip column stays in the
    // table on other tabs (to keep column widths stable) but is inert.
    const dragEnabled = tab === 'Active';

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-display text-lg font-bold text-text">Trial Result Codes</h2>
                <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                    <Plus className="w-3.5 h-3.5" /> Add code
                </Button>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Codes used to score trial and task-analysis data points. <span className="font-medium">Code</span> and
                {' '}<span className="font-medium">Correct</span> are fixed once a code is created so historical data
                stays consistent. Drag the handle on the Active tab to reorder pickers. Archive a code you no longer
                use — it stays in history but drops out of pickers. Archived codes can be restored from the
                Archived tab.
            </p>

            <Card className="p-0 overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div role="tablist" aria-label="Filter codes by status" className="flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
                        {TABS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                role="tab"
                                aria-selected={tab === s}
                                onClick={() => setTab(s)}
                                className={cn(
                                    'px-3 py-1 text-xs rounded transition-colors',
                                    tab === s ? 'bg-surface text-text shadow-xs font-medium' : 'text-text-muted hover:text-text',
                                )}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {rows.length} {rows.length === 1 ? 'code' : 'codes'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load result codes</p>
                        <p className="text-xs text-text-subtle">{extractErrorMessage(error, 'Please try again.')}</p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>{data && data.length > 0 ? 'No result codes match the current filter.' : 'No result codes yet.'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="w-8" aria-label="Drag handle" />
                                    <th className="px-3 py-2 font-bold w-20">Code</th>
                                    <th className="px-3 py-2 font-bold">Label</th>
                                    <th className="px-3 py-2 font-bold w-28">Correct</th>
                                    <th className="px-3 py-2 font-bold w-28">Status</th>
                                    <th className="px-3 py-2 font-bold w-28 text-right">Actions</th>
                                </tr>
                            </thead>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={dragEnabled ? activeRows.map((r) => r.id) : []}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <tbody>
                                        {rows.map((r) => (
                                            <ResultCodeRow
                                                key={r.id}
                                                row={r}
                                                draggable={dragEnabled && !r.archivedAt}
                                                onEdit={() => setEditing(r)}
                                                onArchive={() => setConfirmArchive(r)}
                                                onRestore={() => restoreM.mutate(r.id)}
                                                restoring={restoreM.isPending}
                                            />
                                        ))}
                                    </tbody>
                                </SortableContext>
                            </DndContext>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <ResultCodeDialog
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSubmit={async (payload) => {
                        if (editing) {
                            await updateM.mutateAsync({
                                id: editing.id,
                                payload: { label: payload.label, color: payload.color || null },
                            });
                        } else {
                            await createM.mutateAsync({
                                code: payload.code,
                                label: payload.label,
                                isCorrect: payload.isCorrect,
                                color: payload.color || null,
                            });
                        }
                    }}
                />
            )}

            {confirmArchive && (
                <ArchiveConfirmDialog
                    row={confirmArchive}
                    onClose={() => setConfirmArchive(null)}
                    onConfirm={async () => {
                        await archiveM.mutateAsync(confirmArchive.id);
                        setConfirmArchive(null);
                    }}
                />
            )}
        </div>
    );
}

function ResultCodeRow({
    row,
    draggable,
    onEdit,
    onArchive,
    onRestore,
    restoring,
}: {
    row: TrialResultCode;
    draggable: boolean;
    onEdit: () => void;
    onArchive: () => void;
    onRestore: () => void;
    restoring: boolean;
}) {
    // useSortable must still be called even when draggable is false so hook
    // order stays stable across tab switches — we just don't attach the
    // listeners in that case.
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: row.id,
        disabled: !draggable,
    });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={cn(
                'border-b border-border last:border-b-0 transition-colors',
                !isDragging && 'hover:bg-surface-muted',
                isDragging && 'bg-surface-muted',
            )}
        >
            <td className="w-8 pl-2 align-middle">
                {draggable ? (
                    <button
                        type="button"
                        aria-label={`Reorder ${row.code}`}
                        className="p-1 text-text-subtle hover:text-text cursor-grab active:cursor-grabbing touch-none"
                        {...attributes}
                        {...listeners}
                    >
                        <GripVertical className="w-3.5 h-3.5" />
                    </button>
                ) : (
                    <span className="inline-block w-5" />
                )}
            </td>
            <td className="px-3 py-2">
                <CodeChip row={row} />
            </td>
            <td className="px-3 py-2">{row.label}</td>
            <td className="px-3 py-2">
                <StatusBadge status={row.isCorrect ? 'Correct' : 'Incorrect'} />
            </td>
            <td className="px-3 py-2">
                <StatusBadge status={row.archivedAt ? 'Archived' : 'Active'} />
            </td>
            <td className="px-3 py-2 text-right">
                {row.archivedAt ? (
                    <Button
                        variant="ghost"
                        size="xs"
                        aria-label={`Restore ${row.code}`}
                        onClick={onRestore}
                        disabled={restoring}
                    >
                        <RotateCcw className="w-3 h-3" /> Restore
                    </Button>
                ) : (
                    <>
                        <Button variant="ghost" size="xs" aria-label={`Edit ${row.code}`} onClick={onEdit} className="mr-1">
                            <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="xs" aria-label={`Archive ${row.code}`} onClick={onArchive}>
                            <Archive className="w-3 h-3" />
                        </Button>
                    </>
                )}
            </td>
        </tr>
    );
}

function CodeChip({ row }: { row: TrialResultCode }) {
    const hasColor = !!row.color;
    return (
        <span
            className={cn(
                'inline-flex min-w-[2.25rem] h-6 px-1.5 rounded text-xs font-bold items-center justify-center',
                !hasColor && row.isCorrect  && 'bg-accent text-text',
                !hasColor && !row.isCorrect && 'bg-warning-soft text-warning',
            )}
            style={hasColor ? { backgroundColor: row.color!, color: '#fff' } : undefined}
        >
            {row.code}
        </span>
    );
}

interface DialogPayload {
    code: string;
    label: string;
    isCorrect: boolean;
    color: string | null;
}

function ResultCodeDialog({
    initial,
    onClose,
    onSubmit,
}: {
    initial: TrialResultCode | null;
    onClose: () => void;
    onSubmit: (p: DialogPayload) => Promise<void>;
}) {
    const isEdit = !!initial;
    const [code, setCode] = useState(initial?.code ?? '');
    const [label, setLabel] = useState(initial?.label ?? '');
    const [isCorrect, setIsCorrect] = useState<boolean>(initial?.isCorrect ?? false);
    const [color, setColor] = useState<string>(initial?.color ?? DEFAULT_RESULT_CODE_COLOR);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!code.trim()) { setErr('Code is required.'); return; }
        if (code.trim().length > 8) { setErr('Code must be 8 characters or fewer.'); return; }
        if (!label.trim()) { setErr('Label is required.'); return; }
        setSaving(true);
        try {
            await onSubmit({
                code: code.trim(),
                label: label.trim(),
                isCorrect,
                color: color || null,
            });
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{isEdit ? `Edit code ${initial.code}` : 'Add result code'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-[110px_1fr] gap-3">
                        <FormField label="Code" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    maxLength={8}
                                    disabled={isEdit}
                                />
                            )}
                        </FormField>
                        <FormField label="Label" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    value={label}
                                    onChange={(e) => setLabel(e.target.value)}
                                    maxLength={80}
                                />
                            )}
                        </FormField>
                    </div>
                    <FormField label="Scoring">
                        {(id) => (
                            <select
                                id={id}
                                value={isCorrect ? 'correct' : 'incorrect'}
                                onChange={(e) => setIsCorrect(e.target.value === 'correct')}
                                disabled={isEdit}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none disabled:opacity-60"
                            >
                                <option value="correct">Counts as correct</option>
                                <option value="incorrect">Counts as incorrect</option>
                            </select>
                        )}
                    </FormField>
                    <FormField label="Chip color" hint="Shown on the cell in the trial grid and the stepper.">
                        {() => {
                            const inPalette = RESULT_CODE_COLORS.some((c) => c.value === color);
                            return (
                                <div role="radiogroup" aria-label="Chip color" className="flex items-center gap-1.5">
                                    {!inPalette && color && (
                                        <button
                                            type="button"
                                            role="radio"
                                            aria-checked
                                            aria-label="Current (custom)"
                                            title="Current (custom)"
                                            className="w-7 h-7 rounded-md border border-border ring-2 ring-accent ring-offset-2 ring-offset-background"
                                            style={{ backgroundColor: color }}
                                        />
                                    )}
                                    {RESULT_CODE_COLORS.map((c) => {
                                        const selected = color === c.value;
                                        return (
                                            <button
                                                key={c.value}
                                                type="button"
                                                role="radio"
                                                aria-checked={selected}
                                                aria-label={c.name}
                                                title={c.name}
                                                onClick={() => setColor(c.value)}
                                                className={cn(
                                                    'w-7 h-7 rounded-md border border-border transition-shadow',
                                                    selected && 'ring-2 ring-accent ring-offset-2 ring-offset-background',
                                                )}
                                                style={{ backgroundColor: c.value }}
                                            />
                                        );
                                    })}
                                </div>
                            );
                        }}
                    </FormField>
                    {isEdit && (
                        <p className="text-xs text-text-subtle">
                            Code and scoring cannot be changed on an existing row. Archive and create a new code instead.
                        </p>
                    )}
                    {err && <p className="text-xs text-error">{err}</p>}
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ArchiveConfirmDialog({
    row,
    onClose,
    onConfirm,
}: {
    row: TrialResultCode;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Archive code {row.code}?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Archiving hides <span className="font-medium text-text">{row.code} — {row.label}</span> from new
                    trial and task-analysis entries. Historical data that used this code stays intact. You can restore
                    it later from the Archived tab.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try { await onConfirm(); }
                            catch (e) { setErr(extractErrorMessage(e, 'Could not archive.')); }
                            finally { setWorking(false); }
                        }}
                    >
                        {working ? 'Archiving…' : 'Archive'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
